import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { BasicCardComponent, UiComponentsModule } from '@mwe/ui/components';
import { TextFieldComponent } from '@shared-ikp/components/text-field/text-field.component';
import dayjs from 'dayjs';
import { DATE_FORMAT_GERMAN } from '@shared-ikp/app.constants';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import { UmmeldungStore } from '../../ummeldung.store';
import { FALLBACK_VALUE, mapUmmeldungAdresseToAddress, stepFormOf } from '../../utils';
import { getZaehlpunktnummerShort } from '../../../meter-point-change-request/meter-point-change-request.utils';
import { EditInputsButtonComponent } from '../edit-inputs-button/edit-inputs-button.component';
import { Router } from '@angular/router';
import { WizardContext } from '@mwe/features/wizard';
import { ROUTE_UMMELDUNG_PRODUCT, ROUTE_UMMELDUNG_WIZARD } from '../../../../app.constants';

@Component({
  selector: 'ikp-ummeldung-product-overview-card',
  imports: [BasicCardComponent, TextFieldComponent, TranslatePipe, UiComponentsModule, EditInputsButtonComponent],
  templateUrl: './product-overview-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductOverviewCardComponent {
  private readonly ummeldungStore = inject(UmmeldungStore);
  private readonly router = inject(Router);
  private readonly wizardContext = inject(WizardContext);

  readonly dataTestIdPrefix = input('ummeldung-bisheriger-vertragspartner');
  readonly frozen = input(false);
  readonly showIncompleteNotification = input(false);

  private readonly aktuelleProduktdaten = computed(() => this.ummeldungStore.activeUmmeldung()?.produktdaten?.at(0));

  protected readonly isSmartMeter = computed(() => !!this.aktuelleProduktdaten()?.isSmartMeter);
  protected readonly produkt = computed(() => this.aktuelleProduktdaten()?.produkt?.toLowerCase() || FALLBACK_VALUE);
  protected readonly zaehlernummer = computed(() => this.aktuelleProduktdaten()?.zaehlerNr || FALLBACK_VALUE);
  protected readonly zaehlpunktnummerFormatted = computed(
    () => getZaehlpunktnummerShort(this.aktuelleProduktdaten()?.zaehlpunktnummer) || FALLBACK_VALUE,
  );

  protected readonly adresseFormatted = computed(
    () => getAddressStreetAndPostCodeFormatted(mapUmmeldungAdresseToAddress(this.aktuelleProduktdaten()?.adresse)) || FALLBACK_VALUE,
  );

  protected readonly zaehlerstandInfo = computed(() => {
    const aktuelleProduktdaten = this.aktuelleProduktdaten();

    if (!aktuelleProduktdaten) {
      return FALLBACK_VALUE;
    }

    if (this.isSmartMeter()) {
      return 'ummeldung.step.currentContractPartner.metercountInfotext';
    }

    return aktuelleProduktdaten.zaehlerstand !== null && aktuelleProduktdaten.zaehlerstand !== undefined
      ? `${aktuelleProduktdaten.zaehlerstand} kWh`
      : FALLBACK_VALUE;
  });

  protected readonly dateInfo = computed(() => {
    const ummeldedatum = this.ummeldungStore.activeUmmeldung()?.ummeldedatum;

    return {
      dataTestId: this.isSmartMeter() ? `${this.dataTestIdPrefix()}-ummeldedatum` : `${this.dataTestIdPrefix()}-ablesedatum`,
      label: this.isSmartMeter() ? 'ummeldung.step.currentContractPartner.changeDate' : 'ummeldung.step.currentContractPartner.readingDate',
      value: ummeldedatum ? dayjs(ummeldedatum).format(DATE_FORMAT_GERMAN) : FALLBACK_VALUE,
    };
  });

  protected navigateToProduktStep(): Promise<boolean> {
    stepFormOf(this.wizardContext)?.commitDraft();
    return this.router.navigate([ROUTE_UMMELDUNG_WIZARD, ROUTE_UMMELDUNG_PRODUCT]);
  }
}
